import { delay } from 'base/helpers';
import { COMPONENT_RENDERER_INNER_DATASET_KEY } from 'blocks/component_renderer/component_renderer';
import { isNavbarWithDropdowns } from 'blocks/component_renderer/navbars';
import classNames from 'classnames';
import { expr } from 'mobx-utils';
import React from 'react';
import { editorModeStore, isWireframeOrDesign } from '../editor_mode';
import { unrefSection } from '../model_helpers';
import { globalElementViewOptions } from '../wireframe/element_view_options';
import {
  FrameOpts,
  globalSelectionFrameRegistry,
  globalSelectionHoverRegistry,
} from './frame';
import {
  SELECTABLE_ATTACH_FRAME_ID_ATTR,
  SELECTABLE_OR_FRAME_ID_ATTR,
} from './measurer';
import styles from './selectable.css';
import { idForSelectionState, SelectionState, statesEqual } from './states';
import { SelectionStore } from './store';

type ClickToSelectOpts = {
  onSelect?: () => void;
  ensureVisibleOnSelect?: boolean;
  showOutline?: boolean;
  clickEventOnly?: boolean;
};

function addClickToSelectEffect(
  el: HTMLElement,
  store: SelectionStore,
  getTarget: SelectionState | (() => SelectionState),
  opts?: ClickToSelectOpts,
) {
  async function onClick(event: Event) {
    const target = typeof getTarget === 'function' ? getTarget() : getTarget;
    if (!(event.target instanceof HTMLElement)) return;
    const closestSelectable = event.target.closest(
      `[${SELECTABLE_OR_FRAME_ID_ATTR}]`,
    );
    if (closestSelectable != null && closestSelectable !== el) {
      // a child selectable is handling this
      return;
    }
    const windowSelection = window.getSelection();
    if (
      event.type === 'click' &&
      windowSelection &&
      windowSelection.type !== 'None' &&
      opts?.clickEventOnly !== true
    ) {
      // User is probably selecting text.
      // People often drag outside the textarea when selecting text.  This causes a click event
      // to fire on the element outside the textarea.  (Crazy, right?)
      return;
    }

    // Special case for clicking in a navbar section.
    if (
      target.section &&
      !target.element &&
      isWireframeOrDesign(editorModeStore.get())
    ) {
      // A section has been clicked.
      const section = unrefSection(target.section, store.project);
      const rootElement = section?.element.current;
      if (
        rootElement &&
        isNavbarWithDropdowns(rootElement.source.slug) &&
        // Check that the section-wide hamburger menu isn't open.
        !globalElementViewOptions.get(rootElement)?.openMenus
      ) {
        const closestComponentInner = el.querySelector(
          `[${COMPONENT_RENDERER_INNER_DATASET_KEY}]`,
        );
        if (!closestComponentInner || !(event instanceof MouseEvent)) {
          return;
        }
        const bounds = closestComponentInner.getBoundingClientRect();
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        ) {
          // Click was outside of the component inner. Don't select the
          // section, because that would unselect the dropdown that's
          // currently open. (Dropdowns are going to be rendered inside the
          // component outer's bounds, but outside the component inner's
          // bounds...)
          return;
        }
      }
    }

    const lastFrameSelection = store.get();
    await delay(0);
    if (!statesEqual(store.get(), lastFrameSelection)) {
      // another event handler did something and changed the selection
      // e.g. the user clicked the "add page" button
      return;
    }
    store.set(target, {
      disableEnsureVisible: !opts?.ensureVisibleOnSelect,
    });
    opts?.onSelect?.();
  }

  el.tabIndex = 0;
  el.addEventListener('click', onClick);
  if (opts?.clickEventOnly !== true) {
    el.addEventListener('contextmenu', onClick);
    el.addEventListener('focusin', onClick);
  }

  return () => {
    el.removeEventListener('click', onClick);
    if (opts?.clickEventOnly !== true) {
      el.removeEventListener('contextmenu', onClick);
      el.removeEventListener('focusin', onClick);
    }
  };
}

export function useClickOrHoverToSelect(
  targetRef: React.RefObject<HTMLElement>,
  store: SelectionStore,
  target: SelectionState,
  opts?: ClickToSelectOpts,
) {
  React.useEffect(
    () => {
      const el = targetRef.current;
      if (!el) return;
      return addClickToSelectEffect(el, store, target, opts);
    }, // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [target, targetRef, opts],
  );

  React.useEffect(() => {
    const el = targetRef.current;
    if (!el) return;
    return globalSelectionHoverRegistry.attach(target, el, 'related');
  }, [target, targetRef]);
}

export function useSelectable(
  targetRef: React.RefObject<HTMLElement>,
  store: SelectionStore,
  target: SelectionState,
  {
    onSelect,
    attachFrameTo: attachFrameToProp,
    ...passthrough
  }: FrameOpts & {
    onSelect?: () => void;
    attachFrameTo?: React.RefObject<HTMLDivElement>;
  } = {},
): [isSelected: boolean, className: string] {
  const targetId = idForSelectionState(target);
  const isSelected = expr(() => targetId === store.getId());

  React.useEffect(() => {
    return globalSelectionFrameRegistry.register(targetId, passthrough);
  }, [targetId, passthrough]);

  const attachFrameTo = attachFrameToProp || targetRef;
  React.useEffect(() => {
    const el = attachFrameTo.current;
    if (!el) return;
    el.setAttribute(SELECTABLE_ATTACH_FRAME_ID_ATTR, targetId);
    return () => {
      if (el.getAttribute(SELECTABLE_ATTACH_FRAME_ID_ATTR) === targetId) {
        el.removeAttribute(SELECTABLE_ATTACH_FRAME_ID_ATTR);
      }
    };
  }, [targetId, attachFrameTo]);

  React.useEffect(
    () => {
      const el = targetRef.current;
      if (!el) return;
      if (isSelected) return; // no need to bind listeners then
      return addClickToSelectEffect(el, store, target, { onSelect });
    }, // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [target, isSelected, onSelect],
  );

  React.useEffect(() => {
    const el = targetRef.current;
    if (!el) return;
    return globalSelectionHoverRegistry.attach(target, el, 'original');
  }, [target, targetRef]);

  const className = classNames(store.canSelect && styles.selectable);

  return [isSelected, className];
}
